import React, { useEffect, useState } from "react";
import {
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
  PaintBrushIcon,
  SparklesIcon,
  Squares2X2Icon,
  SwatchIcon,
} from "@heroicons/react/24/outline";

function ControlSlider({
  label,
  value,
  min,
  max,
  unit = "",
  onChange,
  inputId,
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-gray-600 dark:text-gray-400"
        >
          {label}
        </label>
        <span className="rounded-md bg-purple-50 px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
          {value}
          {unit}
        </span>
      </div>
      <input
        id={inputId}
        type="range"
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-purple-700 dark:bg-gray-700"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
      />
    </div>
  );
}

function ColorField({ label, value, onChange, inputId }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
      <label
        htmlFor={inputId}
        className="text-xs font-medium text-gray-600 dark:text-gray-400"
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] uppercase tabular-nums text-gray-500 dark:text-gray-400">
          {value}
        </span>
        <label
          htmlFor={inputId}
          className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md border border-gray-200 shadow-inner dark:border-gray-600"
          style={{ backgroundColor: value }}
        >
          <input
            id={inputId}
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label }) {
  return (
    <div className="mb-2.5 mt-4 flex items-center gap-2 first:mt-0">
      {Icon && (
        <Icon className="h-3.5 w-3.5 text-purple-700 dark:text-purple-400" />
      )}
      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <div className="ms-1 h-px flex-1 bg-gradient-to-l from-transparent to-gray-200 dark:to-gray-700" />
    </div>
  );
}

function CardDesignControls({
  id,
  title,
  state,
  update,
  isActive = false,
  onActivate,
}) {
  const [isOpen, setIsOpen] = useState(state.show);

  // Auto-open when text becomes visible; auto-close when hidden
  useEffect(() => {
    setIsOpen(state.show);
  }, [state.show]);

  const toggleOpen = () => {
    setIsOpen((v) => !v);
    onActivate?.();
  };

  const inputId = (key) => `${id}-${key}`;

  return (
    <div
      onClick={() => onActivate?.()}
      className={`group overflow-hidden rounded-2xl border bg-white shadow-sm transition-all dark:bg-gray-800 ${
        isActive
          ? "border-purple-300 ring-2 ring-purple-500/50 dark:border-purple-700"
          : "border-gray-200 ring-0 dark:border-gray-700"
      }`}
    >
      {/* Header */}
      <div
        className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${
          isActive
            ? "bg-purple-50 dark:bg-purple-950/30"
            : "hover:bg-gray-50 dark:hover:bg-gray-800/60"
        }`}
        onClick={toggleOpen}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            state.show ? "bg-purple-700" : "bg-gray-300 dark:bg-gray-600"
          }`}
        />
        <h3 className="flex-1 text-sm font-extrabold text-gray-800 dark:text-gray-200">
          {title}
        </h3>
        <button
          type="button"
          aria-label={state.show ? "إخفاء النص" : "إظهار النص"}
          onClick={(e) => {
            e.stopPropagation();
            update("show", !state.show);
          }}
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors ${
            state.show
              ? "bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-300"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
          }`}
        >
          {state.show ? (
            <EyeIcon className="h-4 w-4" />
          ) : (
            <EyeSlashIcon className="h-4 w-4" />
          )}
        </button>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Body */}
      {isOpen && state.show && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 dark:border-gray-700">
          {/* Position */}
          <SectionHeader icon={Squares2X2Icon} label="الموضع" />

          <div className="grid grid-cols-2 gap-4">
            <ControlSlider
              inputId={inputId("x")}
              label="أفقي (X)"
              value={state.x}
              min={0}
              max={100}
              unit="%"
              onChange={(v) => update("x", v)}
            />
            <ControlSlider
              inputId={inputId("y")}
              label="رأسي (Y)"
              value={state.y}
              min={0}
              max={100}
              unit="%"
              onChange={(v) => update("y", v)}
            />
          </div>
          <small className="text-xs text-gray-500 dark:text-gray-400">
            يمكنك تغيير موقع النص على الصورة عن طريق السحب والإفلات
          </small>

          {/* Typography */}
          <SectionHeader icon={PaintBrushIcon} label="النص" />
          <div className="space-y-3">
            <ControlSlider
              inputId={inputId("font-size")}
              label="حجم الخط"
              value={state.fontSize}
              min={8}
              max={72}
              unit="px"
              onChange={(v) => update("fontSize", v)}
            />
            <ColorField
              inputId={inputId("color")}
              label="لون النص"
              value={state.color}
              onChange={(v) => update("color", v)}
            />
          </div>

          {/* Border */}
          <SectionHeader icon={SwatchIcon} label="الإطار" />
          <div className="space-y-3">
            <ControlSlider
              inputId={inputId("border-thickness")}
              label="سُمك الإطار"
              value={state.borderThickness}
              min={0}
              max={10}
              unit="px"
              onChange={(v) => update("borderThickness", v)}
            />
            {state.borderThickness > 0 && (
              <ColorField
                inputId={inputId("border-color")}
                label="لون الإطار"
                value={state.borderColor}
                onChange={(v) => update("borderColor", v)}
              />
            )}
          </div>

          {/* Shadow */}
          <SectionHeader icon={SparklesIcon} label="الظل" />
          <label className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              تفعيل الظل
            </span>
            <span className="relative inline-flex">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={state.hasShadow}
                onChange={(e) => update("hasShadow", e.target.checked)}
              />
              <span className="block h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-purple-700 dark:bg-gray-600" />
              <span className="absolute start-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4 rtl:peer-checked:-translate-x-4" />
            </span>
          </label>

          {state.hasShadow && (
            <div className="mt-3 space-y-3 rounded-2xl bg-gray-50/70 p-3 dark:bg-gray-900/40">
              <div className="grid grid-cols-2 gap-4">
                <ControlSlider
                  inputId={inputId("shadow-x")}
                  label="X"
                  value={state.shadowX}
                  min={-10}
                  max={10}
                  unit="px"
                  onChange={(v) => update("shadowX", v)}
                />
                <ControlSlider
                  inputId={inputId("shadow-y")}
                  label="Y"
                  value={state.shadowY}
                  min={-10}
                  max={10}
                  unit="px"
                  onChange={(v) => update("shadowY", v)}
                />
              </div>
              <ControlSlider
                inputId={inputId("shadow-blur")}
                label="التمويه"
                value={state.shadowBlur}
                min={0}
                max={20}
                unit="px"
                onChange={(v) => update("shadowBlur", v)}
              />
              <ColorField
                inputId={inputId("shadow-color")}
                label="لون الظل"
                value={state.shadowColor}
                onChange={(v) => update("shadowColor", v)}
              />
            </div>
          )}
        </div>
      )}

      {/* Hidden state hint */}
      {isOpen && !state.show && (
        <div className="border-t border-dashed border-gray-200 px-4 py-3 text-center text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
          النص مُخفي. فعّل الإظهار لتعديل التصميم.
        </div>
      )}
    </div>
  );
}

export default React.memo(CardDesignControls);
